import dynamic from 'next/dynamic';

import type {
  HeroVideoButton,
  HeroVideoSectionProps,
} from 'src/components/sections/HeroVideoSection';
import type { ValidationError } from 'src/content/types';
import type { SectionRenderer } from 'src/types/Section';
import type { Transformer } from 'src/types/Transformer';
import { fallbackImageUrl } from './fallbackImageUrl';
import type { HeroVideoSectionContent, MuxVideoField } from './types';
import { validateHeroVideo } from './validation';

/*
 * Still crops per breakpoint, built server side so they land in the SSR HTML: the still is the
 * LCP element and must not wait on the player. Small screens get a genuine 4:5 portrait crop of
 * the 16:9 asset (the Figma mobile frame is 1080x1350); large screens the full-width 16:9 frame.
 */
const SMALL_STILL = { height: 960, width: 768 };
const LARGE_STILL = { height: 528, width: 1408 };

/** `{ key: value }`, or nothing at all when the value is absent: getServerSideProps rejects undefined. */
const omitWhenUndefined = <Key extends string, Value>(key: Key, value: Value | undefined) =>
  (value === undefined ? {} : { [key]: value }) as { [K in Key]?: Value };

const playbackId = (field: MuxVideoField | undefined): string =>
  (Array.isArray(field) ? field[0]?.data?.playback_ids?.[0]?.id : undefined) ?? '';

/* istanbul ignore next */
export const renderer: SectionRenderer<HeroVideoSectionProps> = dynamic<HeroVideoSectionProps>(() =>
  import('src/components/sections/HeroVideoSection').then((mod) => mod.HeroVideoSection),
);

export const transformer: Transformer<HeroVideoSectionContent, HeroVideoSectionProps> = (
  { _metadata, hero_video, tracking_title },
  { positionMetadata },
) => {
  const entry = hero_video?.[0];
  const errors: ValidationError[] = validateHeroVideo(entry);

  const buttons: HeroVideoButton[] = [];

  (entry?.buttons || []).forEach((button) => {
    const href = button.button_reference?.[0]?.url;

    if (!href) {
      errors.push({
        message: `Missing referenced page for hero video button "${button.button_text}" (entry: ${entry?.uid}). Check that the linked page is published.`,
      });

      return;
    }

    buttons.push({ href, text: button.button_text });
  });

  const previewPlaybackId = playbackId(entry?.preview_video);

  return {
    uid: _metadata.uid,
    props: {
      buttons,
      expandedPlaybackId: playbackId(entry?.expanded_video),
      // Empty only on a stale draft in preview mode; validation surfaces that as an error.
      headingLine1: entry?.heading_line_1 ?? '',
      headingPosition: entry?.heading_position ?? 'top',
      // Drives the top spacing: nothing above us to space off the header when we lead the page.
      isFirstSection: positionMetadata.position === 0,
      previewPlaybackId,
      stillImageUrlLarge: fallbackImageUrl(
        previewPlaybackId,
        LARGE_STILL.width,
        LARGE_STILL.height,
      ),
      stillImageUrlSmall: fallbackImageUrl(
        previewPlaybackId,
        SMALL_STILL.width,
        SMALL_STILL.height,
      ),
      trackingTitle: tracking_title,
      // Editors name the film in the entry title; the label GA4 and Mux Data report it under.
      videoTitle: entry?.title ?? '',
      ...omitWhenUndefined('endScreenHeading', entry?.end_screen_heading),
      ...omitWhenUndefined('headingLine2', entry?.heading_line_2),
      ...omitWhenUndefined('paragraph', entry?.paragraph),
      ...omitWhenUndefined('termsAndConditions', entry?.terms_and_conditions),
    },
    errors,
  };
};
